/**
 * Push Notifications Service – Opt-in, Subscribe, Dispatch.
 *
 * Push-Events werden im Service Worker (sw.js) empfangen und als
 * Browser-Notifications angezeigt. Dieser Service läuft im Haupt-Thread.
 *
 * Backend-Endpunkte (seit NT-PUSH-BACKEND-01, Push-Kette 2/5):
 *   GET  /api/v1/push/vapid-public-key → öffentlicher VAPID-Key
 *   POST /api/v1/push/subscribe        → Subscription speichern
 *   POST /api/v1/push/unsubscribe      → Subscription löschen
 *
 * Kategorie-Toggles + Permission-on-first-toggle-Flow leben in
 * apps/frontend/src/pages/settings/settings.ts (NT-PUSH-PREFS-UI-01, Push-Kette 3/5) — dieser
 * Service liefert nur die Browser-/Subscription-Primitive, keinen UI-Zustand mehr. Der frühere,
 * rein clientseitige (localStorage-basierte) Master-Opt-in in profile.ts ist damit entfallen —
 * `pushPrefs` (Backend) ist seitdem die einzige Wahrheit dafür, ob Push aktiv ist.
 */

import { api } from './api.js';

// ─── Hilfsfunktionen ─────────────────────────────────────────────────────────

/** VAPID Application Server Key aus Base64URL in Uint8Array konvertieren. */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from(rawData, (char) => char.charCodeAt(0));
}

/** Aktive ServiceWorker-Registration abrufen. */
async function getRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
}

// ─── Exportierte Service-Funktionen ──────────────────────────────────────────

/**
 * Browser-Benachrichtigungserlaubnis anfragen.
 * Darf nur nach einer Nutzer-Geste aufgerufen werden (Browser-Pflicht).
 *
 * @returns true wenn Erlaubnis erteilt, false wenn abgelehnt oder nicht verfügbar
 */
export async function requestPushPermission(): Promise<boolean> {
  if (!('Notification' in window)) return false;

  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;

  const result = await Notification.requestPermission();
  return result === 'granted';
}

/** Aktueller Browser-Permission-Status, robust für Browser ohne Notification-API. */
export function getPushPermissionState(): NotificationPermission | 'unsupported' {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

/**
 * Push-Subscription anlegen (falls noch keine existiert) und ans Backend senden. Holt den
 * öffentlichen VAPID-Key vom Backend (GET /push/vapid-public-key, Single Source of Truth statt
 * einem separat zu pflegenden VITE_-Build-Env) und meldet der Aufruferin per Rückgabewert, ob am
 * Ende wirklich eine Subscription besteht — die Prefs-UI darf `pushPrefs` nur dann persistieren,
 * wenn das hier wirklich geklappt hat.
 *
 * Setzt voraus dass requestPushPermission() bereits true zurückgegeben hat.
 */
export async function subscribeToPush(): Promise<boolean> {
  const registration = await getRegistration();
  if (!registration) {
    console.warn('[Push] Kein Service Worker aktiv – Push nicht verfügbar.');
    return false;
  }

  const existing = await registration.pushManager.getSubscription();
  if (existing) return true;

  let publicKey: string;
  try {
    const res = await api.get<{ publicKey: string | null }>('/push/vapid-public-key');
    if (!res.publicKey) {
      console.warn('[Push] Backend hat keinen VAPID-Key konfiguriert – Push nicht verfügbar.');
      return false;
    }
    publicKey = res.publicKey;
  } catch (err) {
    console.warn('[Push] VAPID-Key konnte nicht geladen werden:', err);
    return false;
  }

  let subscription: PushSubscription;
  try {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey) as unknown as ArrayBuffer,
    });
  } catch (err) {
    console.warn('[Push] Subscription fehlgeschlagen:', err);
    return false;
  }

  try {
    await api.post('/push/subscribe', { subscription: subscription.toJSON() });
    return true;
  } catch (err) {
    console.warn('[Push] Subscription konnte nicht ans Backend gemeldet werden:', err);
    return false;
  }
}

/**
 * Bewusst KEIN `unsubscribeFromPush()` mehr: die Prefs-UI (settings.ts) ruft bei
 * Master-aus/letzte-Kategorie-aus NICHT `/push/unsubscribe` bzw. `PushSubscription.unsubscribe()`
 * auf — `pushPrefs` (Backend) ist allein die Wahrheit dafür, ob gesendet wird (Empfehlung aus
 * NT-PUSH-PREFS-UI-01, im Schritt-0-Bericht bestätigt). Reaktivierung bleibt so ein Klick ohne
 * erneuten Browser-Permission-Dialog. Ein echter Geräte-Opt-out ist nicht Teil dieses Prompts.
 */
